/** Device pixels, browser zoom, and the size floor every reading is held to.
 *
 * Nothing in this file touches a DOM element it was not handed, and the pure
 * functions have no window at all, so a test can drive them with any ratio.
 *
 * ## Why a CSS pixel is not a device pixel
 *
 * `window.devicePixelRatio` is the number of device pixels one CSS pixel is
 * worth, and browser zoom moves it: Ctrl+minus once puts Chrome at 80% and the
 * ratio at 0.8. Every layout in this build is authored in CSS pixels, so at 80%
 * a bitmap glyph drawn at magnification 2 asks for 1.6 device pixels per texel
 * and the rasteriser gives six of its seven rows two device rows and the
 * seventh one - the same doubled-scanline failure `snapMagnification` in
 * `pixi.ts` describes, plus a resample on top because the DOM side has no
 * `antialias: false`. Measured on the adventure screenshot before this file
 * existed: the sidebar's `Heroes` (text face, magnification 1, a 9-row cap)
 * stood 9 device rows tall in ONE ink level at 100%; at 80% the same caption
 * carried 18 ink levels and no column of it stood more than 4 rows tall.
 *
 * ## The rule: honour the ratio, snap the glyphs, hold the floor
 *
 * Browser zoom is honoured in BOTH directions. A player who presses Ctrl+minus
 * is asking for a smaller frame - the command column, the plates, the town
 * screen all at four fifths - and gets one; what they do not get is smaller
 * type than the original game ever drew, because every bitmap reading snaps
 * its magnification to a whole number of DEVICE pixels per texel
 * (`snapDeviceMagnification`) and is then held to `MIN_DEVICE_CAP`
 * (`holdToFloor`). At 80% a scale-2 HUD caption that asked for 1.6 device
 * pixels per texel is drawn at 1, promoted to the eleven-row tall face, and
 * lands 11 device rows tall in one ink level - the same rows it has at 100% on
 * a 1366x768 window - inside a panel that is 20% narrower on the glass.
 *
 * It used to compensate zoom OUT instead: the app root was transformed by
 * `1 / devicePixelRatio` so the 80% frame was the 100% frame, pixel for pixel.
 * That kept every measurement honest and made the zoom useless - a player
 * whose window could not show the whole town screen zoomed out and got the
 * same frame back. The compensation is gone; a CSS pixel is worth whatever
 * the browser says it is, and the canvases measure that
 * (`measureDeviceScale`) rather than assuming 1.
 *
 * High-density displays are the same case as zoom IN. A 4K panel at 150% OS
 * scaling reports 1.5 the same way 150% browser zoom does, and neither is
 * pulled back to device pixels: above 1 the ratio is honoured and the glyphs
 * snap their magnification to a whole number of device pixels, the same as
 * below it.
 *
 * ## The floor
 *
 * `MIN_DEVICE_CAP` is the smallest cap height, in device pixels, a reading may
 * be stamped at. It is anchored on the commercial game rather than chosen: the
 * HD Edition at 1920x1080 sets its caption face (`Attack`, `Month: 1, Week: 2`,
 * the status line) at a measured 16 device pixels of cap, and the 800x600
 * original's smallfont scaled to a 768-tall window is 9 * 768 / 600 = 11.5.
 * Eleven is that second figure rounded down - the size the original is read
 * at on the smallest window this build targets - and every face this build
 * ships either meets it at magnification 1 (the display faces) or at 2.
 *
 * ## The floor grows with the window
 *
 * Eleven rows is the floor for a 768-tall window, and it was the floor for
 * every window: on a 1920x1080 monitor at 100% the hero sheet's `Attack`,
 * the sidebar's `Heroes` and the spellbook's `Spell Points: 5` all stood 11
 * device rows tall, 69% of the HD Edition's 16 on the same glass, and a
 * reader named them as the smallest type in the frame. `deviceCapFloor`
 * scales the floor with the DEVICE height of the window - the height in
 * device pixels, so browser zoom does not move it: a 1080-tall window is
 * 1080 device rows at 100% and at 80%, and the floor is 15 in both. Fifteen
 * on 1080 is one row under the HD Edition's own caption and, scaled to 768,
 * is 10.7 - the eleven the floor already was, which is why nothing on a
 * laptop window changes. The `caption` sans face and the `serifCaption` face
 * are the ones that stand 15 rows at magnification 1, so a 1080p promotion
 * lands there and not on a doubled 9-row face at 18.
 */

/** Smallest cap height, in device pixels, any bitmap reading is drawn at, on
 *  any window. `deviceCapFloor` raises it on a tall one. */
export const MIN_DEVICE_CAP = 11;

/** The device height at which the floor is `FLOOR_AT_1080`. */
const FLOOR_REFERENCE_HEIGHT = 1080;

/** The floor on a 1080-tall window. The HD Edition's caption is 16 there;
 *  15 is the tallest whole cap a 20%-narrower 80% layout still holds. */
const FLOOR_AT_1080 = 15;

/**
 * The cap floor, in device pixels, for a window `deviceHeight` device pixels
 * tall: `FLOOR_AT_1080` scaled by the height and rounded, never under
 * `MIN_DEVICE_CAP`. 11 up to 827 device rows, 15 at 1080, 20 at 1440.
 * A missing or nonsense height is the smallest window, not the largest.
 */
export function deviceCapFloor(deviceHeight: number): number {
  if (!(deviceHeight > 0)) return MIN_DEVICE_CAP;
  return Math.max(MIN_DEVICE_CAP, Math.round((FLOOR_AT_1080 * deviceHeight) / FLOOR_REFERENCE_HEIGHT));
}

/**
 * The cap floor for the window this code is running in: its `innerHeight`
 * in device pixels under the ratio the browser reports. `MIN_DEVICE_CAP`
 * with no window, which is what a test and a server render should assume.
 */
export function windowCapFloor(): number {
  const w = globalThis as { innerHeight?: number; devicePixelRatio?: number };
  if (typeof w.innerHeight !== "number" || !(w.innerHeight > 0)) return MIN_DEVICE_CAP;
  const ratio = typeof w.devicePixelRatio === "number" && w.devicePixelRatio > 0 ? w.devicePixelRatio : 1;
  return deviceCapFloor(w.innerHeight * ratio);
}

/**
 * The browser's ratio, made safe to divide by: itself when positive, 1 when
 * the browser reported nothing usable. Browser zoom is honoured in both
 * directions, so this is NOT held at 1 or above - 0.8 at 80% is the answer,
 * and the glyph snap below is what keeps 0.8 from putting a texel on four
 * fifths of a device pixel.
 */
export function effectiveDevicePixelRatio(devicePixelRatio: number): number {
  return devicePixelRatio > 0 ? devicePixelRatio : 1;
}

/**
 * Device pixels per CSS pixel of `el`, MEASURED rather than assumed.
 *
 * `getBoundingClientRect` reports the box in viewport CSS pixels with every
 * ancestor's transform applied, while `clientWidth` is the element's own
 * layout width before it; their ratio is whatever scale sits above the
 * element, and the device ratio on top of that is what a canvas has to render
 * at to land on the glass one-to-one. Nothing in the shipped tree scales a
 * canvas's ancestors any more (the zoom-compensating root transform is gone),
 * so today this is the bare ratio - but a canvas that assumed 1 under a scaled
 * ancestor was measured once as a 1310-wide backing store stretched over 1637
 * device columns, and the measurement is what keeps that from coming back.
 *
 * Falls back to `effectiveDevicePixelRatio` when the element has no layout yet
 * (a canvas mounted before its first frame) or there is no window.
 */
export function measureDeviceScale(el: { getBoundingClientRect(): { width: number }; clientWidth: number } | null): number {
  const dpr = typeof globalThis.devicePixelRatio === "number" && globalThis.devicePixelRatio > 0
    ? globalThis.devicePixelRatio : 1;
  if (el === null || el.clientWidth <= 0) return effectiveDevicePixelRatio(dpr);
  const rect = el.getBoundingClientRect();
  if (!(rect.width > 0)) return effectiveDevicePixelRatio(dpr);
  const measured = (rect.width / el.clientWidth) * dpr;
  // The measurement carries layout rounding (a 1637px box reported as
  // 1636.8); a ratio within a hundredth of a clean value IS that value, and
  // the renderer must not be told 0.9998 when it was 1.
  const snapped = Math.round(measured * 100) / 100;
  return snapped > 0 ? snapped : 1;
}

/** What a DOM bitmap reading is drawn at once the device ratio is accounted for. */
export interface DeviceMagnification {
  /** Whole device pixels per glyph texel. Always an integer >= 1. */
  readonly magnification: number;
  /** CSS pixels per glyph texel: `magnification / ratio`. Fractional under a
   *  fractional ratio, and that is the point - the CSS size is whatever puts
   *  the texel on a whole device pixel. */
  readonly cssPerTexel: number;
}

/**
 * The whole device magnification for `scale` CSS pixels per texel.
 *
 * At a ratio of 1 or more it is ROUNDED, not floored, unlike the arena's
 * `snapMagnification`. The arena floors because its labels are measured into
 * boxes it owns and a label may never outgrow its box; a DOM reading sits in
 * a flex row that grows around it, and flooring 1.25 to 1 would shrink every
 * caption on a 125%-zoomed page to four fifths of its authored size. Rounding
 * keeps the reading within half a device pixel per texel of what the call
 * site asked for.
 *
 * Below 1 - a browser zoomed OUT - it is FLOORED. The player asked for a
 * smaller frame, and rounding 1.6 up to 2 would draw a scale-2 caption at 2.5
 * CSS pixels per texel, a quarter wider than the groove it was measured into
 * at 100% while that groove is itself a fifth narrower on the glass. Flooring
 * takes it to 1 device pixel per texel, and `holdToFloor` then promotes the
 * face so the cap still clears `MIN_DEVICE_CAP`: a HUD caption at 7 * 1.6 =
 * 11.2 asked-for device rows is stamped as the tall face at 11. The floor is
 * what makes flooring safe; without it a zoom-out would shrink type without
 * limit.
 */
export function snapDeviceMagnification(scale: number, deviceRatio: number): DeviceMagnification {
  const ratio = deviceRatio > 0 ? deviceRatio : 1;
  const want = scale * ratio;
  // The epsilon is for the exact cases under a float32-ish ratio: Chromium at
  // 80% reports 0.8000000119, and 2.5 * that must still floor to 2, not 1.
  const magnification = Math.max(1, ratio < 1 ? Math.floor(want + 1e-6) : Math.round(want));
  return { magnification, cssPerTexel: magnification / ratio };
}

/**
 * Where a reading lands once it is held to the floor: which face of an ordered
 * family, and at what whole device magnification.
 *
 * `caps` is a family's cap heights in ascending order - `[7, 9, 11, 11, 15]`
 * for the sans faces, `[11, 14, 15, 19]` for the serif family - and `face` the
 * index the call site asked for. When the asked-for face at the snapped
 * magnification stands below `floor`, the candidates are every face from the
 * asked one up, at every magnification from 1 to a few past the asked one,
 * that stands at least as tall as what was asked AND clears the floor; the
 * winner is the one with the SMALLEST device cap, and between two of one
 * size the one at the LOWER magnification, because a face drawn at 1 is
 * drawn with every texel it was authored with and a face drawn at 2 is a
 * small face doubled. A seven-row HUD reading at magnification 1 becomes the
 * eleven-row tall face at 1 (11 device px) rather than the HUD face at 2
 * (14), because the call site chose a small reading and the floor is the
 * least that can be done to it; a HUD reading at 2 (14 rows) under a floor
 * of 15 becomes the fifteen-row caption face at 1, not the nine-row text
 * face at 2 (18). A face is only ever promoted upward; a reading is never
 * drawn smaller than it asked.
 *
 * ## Under a zoom OUT, the nearest face at 1 comes first
 *
 * Below a ratio of 1 the snap FLOORS the magnification, and flooring a
 * scale-2 title to 1 halves it: the hero's name, 28 device rows at 100%,
 * asked for 22.4 at 80% and was drawn at 14 - then promoted to the 15-row
 * caption face, the same face as the level line under it, so the card lost
 * its hierarchy in one zoom step. The HD Edition at 1080p keeps a 19-row
 * name over a 16-row level line, a 1.2x step, never 2x. So before the floor
 * rule runs, a zoomed-out reading looks for a face of its family that, at
 * magnification 1, stands within `ZOOM_OUT_TOLERANCE` UNDER what it asked
 * for on the glass (`cap * scale * ratio`) and no taller than it stood at
 * 100% (`cap * scale`, so a groove measured at 100% still holds it), and
 * takes the nearest. A serif title at 2 under 0.8 lands on the 19-row title
 * face; a HUD caption at 2 (11.2 asked) lands on the 11-row tall face, which
 * is what the floor gave it anyway; a count at 4 (22.4 asked) has no face
 * within a fifth of that at 1 and keeps the floored HUD face at 3, as it
 * did before this rule. Magnification 1 only: a face drawn at 1 is drawn
 * with every texel it was authored with, and a small face doubled is not a
 * title.
 */
export function holdToFloor(
  caps: readonly number[], face: number, scale: number, deviceRatio: number,
  floor = MIN_DEVICE_CAP,
): { readonly face: number; readonly magnification: number } {
  const ratio = deviceRatio > 0 ? deviceRatio : 1;
  const asked = snapDeviceMagnification(scale, ratio).magnification;
  const cap = caps[face] ?? caps[0] ?? 1;
  const askedDevice = cap * asked;
  if (ratio < 1) {
    const nearest = nearestFaceAtOne(caps, face, cap * scale * ratio, cap * scale, floor);
    if (nearest !== null) return { face: nearest, magnification: 1 };
  }
  if (askedDevice >= floor) return { face, magnification: asked };
  const best = { face, magnification: asked, device: Infinity };
  const consider = (f: number, m: number): void => {
    const device = (caps[f] ?? 1) * m;
    if (device < floor || device < askedDevice) return;
    if (device > best.device || (device === best.device && m >= best.magnification)) return;
    best.face = f; best.magnification = m; best.device = device;
  };
  // Every magnification up to a few past the asked one is enough: a
  // magnification of `asked + floor` puts even a one-row face over the floor.
  for (let m = 1; m <= asked + floor; m++) {
    for (let f = face; f < caps.length; f++) consider(f, m);
  }
  return { face: best.face, magnification: best.magnification };
}

/** How far under its asked-for device cap a zoomed-out reading may land on
 *  a face at magnification 1: 22.4 asked admits 19 (a fifth under is 17.9)
 *  and refuses 15. */
const ZOOM_OUT_TOLERANCE = 0.2;

/**
 * The face of `caps`, from `face` up, that at magnification 1 stands nearest
 * `target` device rows while standing at least `floor`, at least
 * `(1 - ZOOM_OUT_TOLERANCE) * target`, and no more than `ceiling` - or null
 * when no face of the family does. Between two faces one row apart on either
 * side of the target the earlier (narrower) face wins.
 */
function nearestFaceAtOne(
  caps: readonly number[], face: number, target: number, ceiling: number, floor: number,
): number | null {
  const least = Math.max(floor, (1 - ZOOM_OUT_TOLERANCE) * target);
  let best: number | null = null;
  for (let f = face; f < caps.length; f++) {
    const device = caps[f] ?? 1;
    if (device < least || device > ceiling) continue;
    if (best === null || Math.abs(device - target) < Math.abs((caps[best] ?? 1) - target)) best = f;
  }
  return best;
}
